
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Clock, Save, User, Calendar, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useProjects } from '@/hooks/useProjects';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';

interface Employee {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export const ManualTimeEntry: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [description, setDescription] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [hourlyRate, setHourlyRate] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { projects } = useProjects();
  const activeProjects = projects.filter((project) => project.is_active && project.status === 'active');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      let data: any[] | null = null;
      let error: any = null;

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const res = await supabase.rpc('pin_list_profiles', { p_pin: pin });
        data = (res.data as any[]) || [];
        error = res.error;
      } else {
        const res = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email, hourly_rate')
          .eq('is_active', true)
          .order('first_name');
        data = (res.data as any[]) || [];
        error = res.error;
      }

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des employés:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger la liste des employés",
        variant: "destructive",
      });
    }
  };

  const calculateDuration = (start: string, end: string) => {
    const startDate = new Date(`2000-01-01T${start}:00`);
    const endDate = new Date(`2000-01-01T${end}:00`);
    return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEmployee || !selectedProject || !date || !startTime || !endTime) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive",
      });
      return;
    }

    if (startTime >= endTime) {
      toast({
        title: "Erreur",
        description: "L'heure de fin doit être après l'heure de début",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const startDateTime = new Date(`${date}T${startTime}:00`);
      const endDateTime = new Date(`${date}T${endTime}:00`);
      const durationMinutes = calculateDuration(startTime, endTime);

      const timeEntry = {
        user_id: selectedEmployee,
        project_id: selectedProject,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        duration_minutes: durationMinutes,
        description: description || null,
        is_billable: isBillable,
        hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
      };

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_create_time_entry_for_employee', {
          p_pin: pin,
          p_user_id: timeEntry.user_id,
          p_project_id: timeEntry.project_id,
          p_start_time: timeEntry.start_time,
          p_end_time: timeEntry.end_time,
          p_duration_minutes: timeEntry.duration_minutes,
          p_description: timeEntry.description,
          p_is_billable: timeEntry.is_billable,
          p_hourly_rate: timeEntry.hourly_rate,
          p_status: 'closed',
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('time_entries').insert([timeEntry]);
        if (error) throw error;
      }

      toast({
        title: "Succès",
        description: `Entrée de temps créée: ${Math.round(durationMinutes / 6) / 10}h`,
      });

      // Reset form
      setSelectedEmployee('');
      setSelectedProject('');
      setDescription('');
      setHourlyRate('');
      setStartTime('09:00');
      setEndTime('17:00');
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer l'entrée de temps",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (employee: Employee) => {
    return `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.email || 'Nom inconnu';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Entrée de temps manuelle</h1>
            <p className="text-muted-foreground">Créer une entrée de temps pour un employé</p>
          </div>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Nouvelle entrée de temps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Employee Selection */}
              <div className="space-y-2">
                <Label htmlFor="employee">
                  <User className="h-4 w-4 inline mr-2" />
                  Employé *
                </Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un employé" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {getEmployeeName(employee)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Project Selection */}
              <div className="space-y-2">
                <Label htmlFor="project">
                  <FileText className="h-4 w-4 inline mr-2" />
                  Projet *
                </Label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un projet" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeProjects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name} - {project.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label htmlFor="date">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Date *
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>

              {/* Time Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startTime">Heure de début *</Label>
                  <Input
                    id="startTime"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endTime">Heure de fin *</Label>
                  <Input
                    id="endTime"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Duration Display */}
              {startTime && endTime && startTime < endTime && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-sm font-medium text-blue-900">
                    Durée calculée: {Math.round(calculateDuration(startTime, endTime) / 6) / 10} heures
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Décrivez le travail effectué..."
                  rows={3}
                />
              </div>

              {/* Billing Options */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="billable"
                    checked={isBillable}
                    onChange={(e) => setIsBillable(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Label htmlFor="billable">Temps facturable</Label>
                </div>

                {isBillable && (
                  <div className="space-y-2">
                    <Label htmlFor="hourlyRate">Taux horaire (€/h)</Label>
                    <Input
                      id="hourlyRate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value)}
                      placeholder="Laisser vide pour utiliser le taux par défaut"
                    />
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={onBack}>
                  Annuler
                </Button>
                <Button type="submit" disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Création...' : 'Créer l\'entrée'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
